import type {
  DashboardComponent,
  DashboardDefinition,
  Display,
  Position,
  TableComponent,
} from 'common/types/dashboard/dashboardDefinition';
import type { Filter, SortKey, SummaryGroupBy } from 'common/types/summaryDefinitions';
import { DEFAULT_GRID_COLUMNS } from 'pages/Intelligence/SummaryDashboard/components/DashboardGrid/DashboardGrid.styles';

// Mirrors dashboard_schema.py: MAX_GRID_COLUMNS, MAX_TAGS, and the list `limit` bounds.
export const MIN_GRID_COLUMNS = 1;
export const MAX_GRID_COLUMNS = 12;
export const MAX_TAGS = 10;
export const MIN_LIST_LIMIT = 1;
export const MAX_LIST_LIMIT = 10;

const COMPONENT_ID_PREFIX = 'cmp_';

type NonEmptyArray<T> = [T, ...T[]];

// The backend keeps any id matching ^cmp_ that is not a duplicate (_normalize_component), so an id
// minted here survives the save untouched. That is what lets the tab strip key off component.id
// from the first render: an index-derived fallback silently re-points the active tab at a different
// component the moment the array shifts under add / delete / reorder.
const newComponentId = (taken: Set<string>): string => {
  for (;;) {
    const candidate = `${COMPONENT_ID_PREFIX}${Math.random().toString(16).slice(2, 10).padEnd(8, '0')}`;
    if (!taken.has(candidate)) {
      taken.add(candidate);
      return candidate;
    }
  }
};

// TS cannot prove that spreading a union member and overwriting an optional key yields that same
// member, but `display`, `styling`, `position` and `id` are declared identically on all seven
// component interfaces, so it does. One cast here beats seven switch arms.
const withEnvelope = <C extends DashboardComponent>(
  component: C,
  patch: { display?: Display; id?: string; position?: Position }
): C => ({ ...component, ...patch }) as C;

// `@minItems 1` compiles to a non-empty tuple, so a computed string[] is not assignable. Rebuilding
// the tuple is the honest conversion; undefined means "do not write", which validateDraft has
// already reported as a missing required field.
export const asNonEmpty = <T,>(values: readonly T[]): NonEmptyArray<T> | undefined =>
  values.length ? [values[0], ...values.slice(1)] : undefined;

// `@maxItems 10` compiles to a union of eleven fixed-length tuples, which no array operation can
// produce. Truncating and casting is the only way to write it; validateDraft reports the overflow
// rather than letting it pass silently the way the backend's own truncation does.
export const asTags = (values: readonly string[]): NonNullable<Display['tags']> =>
  values.slice(0, MAX_TAGS) as NonNullable<Display['tags']>;

// The backend drops blank strings and empty containers on the way in, so a stored '' comes back as
// an absent key. Removing it up front keeps the draft comparable with what the save returns.
export const withOptional = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  value: T[K] | undefined
): T => {
  if (value === undefined || value === null) {
    const { [key]: _removed, ...rest } = target;
    return rest as T;
  }
  return { ...target, [key]: value };
};

// Stores the raw string so a user can type a space mid-word; pruneDraftForSave does the final trim.
export const withOptionalText = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  raw: string
): T => withOptional(target, key, (raw.trim() ? raw : undefined) as T[K] | undefined);

export const withOptionalList = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  values: readonly unknown[]
): T => withOptional(target, key, (values.length ? values : undefined) as T[K] | undefined);

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const readGridColumns = (definition: DashboardDefinition): number =>
  definition.layout?.columns ?? DEFAULT_GRID_COLUMNS;

// Row-flow packing, the same rule flexbox wrap uses: fill left to right, break when the next tile
// will not fit, advance by the tallest tile in the row just closed. Deterministic, overlap-free,
// and `x + w <= columns` holds by construction — which is the one position rule the backend treats
// as a hard error. Running it after every structural change is also what closes the hole a delete
// would otherwise leave, and what keeps a reduced column count from stranding a component on a tab
// the user is not looking at.
export const repackPositions = (definition: DashboardDefinition): DashboardDefinition => {
  const columns = readGridColumns(definition);
  let cursorX = 0;
  let cursorY = 0;
  let rowHeight = 0;

  return {
    ...definition,
    components: definition.components.map(component => {
      const { h = 1, w = 1 } = component.position ?? {};
      const width = clamp(w, 1, columns);
      const height = Math.max(h, 1);

      if (cursorX + width > columns) {
        cursorX = 0;
        cursorY += rowHeight;
        rowHeight = 0;
      }

      const position: Position = { x: cursorX, y: cursorY, w: width, h: height };
      cursorX += width;
      rowHeight = Math.max(rowHeight, height);

      return withEnvelope(component, { position });
    }),
  };
};

const DEFAULT_POSITION: Position = { x: 0, y: 0, w: 1, h: 1 };

// Every default has to satisfy its type's required_field_keys on the very first save, because the
// dialog persists the whole definition in one PATCH and one invalid component rejects all of them.
export const createComponent = (type: DashboardComponent['type']): DashboardComponent => {
  switch (type) {
    case 'totals':
      return {
        type,
        position: DEFAULT_POSITION,
        display: { title: 'Totals' },
        fields: { metrics: ['total_value'] },
      };
    case 'bar_chart':
      return {
        type,
        position: DEFAULT_POSITION,
        display: { title: 'Bar chart' },
        fields: { dimension: 'company_name', metrics: ['total_value'] },
      };
    case 'line_chart':
      // measurement_date + measurement_dates is the only pairing that reads as a time series. It is
      // a dimension but not a metric, which is why the two vocabularies are separate lists.
      return {
        type,
        position: DEFAULT_POSITION,
        display: { title: 'Line chart' },
        fields: {
          dimension: 'measurement_date',
          metrics: ['total_value'],
          series_source: 'measurement_dates',
        },
      };
    case 'pie_chart':
      return {
        type,
        position: DEFAULT_POSITION,
        display: { title: 'Pie chart' },
        fields: { dimension: 'company_name', metric: 'total_value' },
      };
    case 'table':
      // Always the spreadsheet_definition branch: _validate_table collapses the `columns` branch
      // into exactly this shape, so writing it directly keeps the draft equal to what comes back.
      return {
        type,
        position: DEFAULT_POSITION,
        display: { title: 'Table' },
        fields: {
          spreadsheet_definition: {
            columns: { 0: 'company_name', 1: 'total_value' },
            groupBy: 'company',
            sortKeys: [],
            filters: [],
            filterExclusions: [],
          },
        },
      };
    case 'content_card':
      // The only type with no required fields, and the only one whose `fields` is optional.
      return { type, position: DEFAULT_POSITION, display: { title: 'Content' } };
    case 'list':
      return {
        type,
        position: DEFAULT_POSITION,
        display: { title: 'Ranked list' },
        fields: { metric: 'total_value', entity_kind: 'company', direction: 'desc', limit: 5 },
      };
    default: {
      // Exhaustive by construction: a new backend component type regenerates DashboardComponent and
      // stops this file compiling until it gets a default.
      const unhandled: never = type;
      return unhandled;
    }
  }
};

export const collectComponentIds = (components: readonly DashboardComponent[]): Set<string> =>
  new Set(components.map(component => component.id).filter((id): id is string => Boolean(id)));

// Seeds the editable draft. Null for a definition whose dashboard_definition was never written; it
// opens as an empty dashboard rather than refusing to open.
export const seedDraft = (definition: DashboardDefinition | null | undefined): DashboardDefinition => {
  const base: DashboardDefinition = definition ?? {
    version: 1,
    layout: { columns: DEFAULT_GRID_COLUMNS },
    components: [],
  };
  const taken = collectComponentIds(base.components);

  return repackPositions({
    ...base,
    layout: { columns: readGridColumns(base) },
    components: base.components.map(component =>
      component.id ? component : withEnvelope(component, { id: newComponentId(taken) })
    ),
  });
};

export const addComponent = (
  definition: DashboardDefinition,
  type: DashboardComponent['type']
): { definition: DashboardDefinition; id: string } => {
  const id = newComponentId(collectComponentIds(definition.components));
  const component = withEnvelope(createComponent(type), { id });

  return {
    definition: repackPositions({
      ...definition,
      components: [...definition.components, component],
    }),
    id,
  };
};

export const deleteComponent = (
  definition: DashboardDefinition,
  id: string
): DashboardDefinition =>
  repackPositions({
    ...definition,
    components: definition.components.filter(component => component.id !== id),
  });

export const replaceComponent = (
  definition: DashboardDefinition,
  index: number,
  next: DashboardComponent
): DashboardDefinition =>
  // Repacked because a width or height edit changes where every later tile lands.
  repackPositions({
    ...definition,
    components: definition.components.map((component, i) => (i === index ? next : component)),
  });

export const moveComponent = (
  definition: DashboardDefinition,
  fromIndex: number,
  toIndex: number
): DashboardDefinition => {
  const components = [...definition.components];
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= components.length || toIndex >= components.length) {
    return definition;
  }
  const [moved] = components.splice(fromIndex, 1);
  components.splice(toIndex, 0, moved);

  return repackPositions({ ...definition, components });
};

export const setGridColumns = (
  definition: DashboardDefinition,
  columns: number
): DashboardDefinition =>
  repackPositions({ ...definition, layout: { columns } });

// ---------------------------------------------------------------------------
// Table component — the embedded spreadsheet
// ---------------------------------------------------------------------------

export interface TableSpreadsheet {
  columns: string[];
  filters: Filter[];
  groupBy: SummaryGroupBy;
  sortKeys: SortKey[];
}

const isSpreadsheetBranch = (
  fields: TableComponent['fields']
): fields is Extract<TableComponent['fields'], { spreadsheet_definition: unknown }> =>
  'spreadsheet_definition' in fields;

// Values are read as unknown despite the generated `string` type: rows written before the dashboard
// validator tightened up can still hold objects like {"0": {"metric": "moic"}}, and legacy rows
// carry {"key": …, "direction": …} sort entries. Both are dropped rather than crashing the editor.
const readColumns = (columns: Record<string, unknown> | undefined): string[] =>
  Object.entries(columns ?? {})
    .sort(([left], [right]) => Number(left) - Number(right))
    .map(([, value]) => value)
    .filter((value): value is string => typeof value === 'string');

const readSortKeys = (entries: unknown[][] | undefined): SortKey[] =>
  (entries ?? [])
    .filter((entry): entry is unknown[] => Array.isArray(entry) && entry.length === 2)
    .filter(([column, direction]) => typeof column === 'string' && (direction === 'asc' || direction === 'desc'))
    .map(([column, direction]) => [column, direction] as SortKey);

const readFilters = (entries: unknown[][] | undefined): Filter[] =>
  (entries ?? [])
    .filter((entry): entry is unknown[] => Array.isArray(entry) && entry.length === 2)
    .filter(([column, constraint]) => typeof column === 'string' && Array.isArray(constraint) && constraint.length === 2)
    .map(([column, constraint]) => [column, constraint] as Filter);

export const readTableSpreadsheet = (fields: TableComponent['fields']): TableSpreadsheet => {
  const source = isSpreadsheetBranch(fields) ? fields.spreadsheet_definition : fields;
  const rawGroupBy = isSpreadsheetBranch(fields) ? fields.spreadsheet_definition.groupBy : fields.group_by;
  const groupBy = rawGroupBy === 'fund' ? 'fund' : 'company';

  return {
    columns: readColumns(source.columns as Record<string, unknown> | undefined),
    filters: readFilters(source.filters),
    groupBy,
    sortKeys: readSortKeys(source.sortKeys),
  };
};

// filterExclusions and columnWidths are whitelisted straight through by
// normalize_spreadsheet_definition, so they are carried over rather than dropped.
export const writeTableSpreadsheet = (
  component: TableComponent,
  next: TableSpreadsheet
): TableComponent => {
  const previous = isSpreadsheetBranch(component.fields)
    ? component.fields.spreadsheet_definition
    : undefined;

  return {
    ...component,
    fields: {
      spreadsheet_definition: {
        ...previous,
        columns: Object.fromEntries(next.columns.map((column, index) => [index, column])),
        groupBy: next.groupBy,
        sortKeys: next.sortKeys,
        filters: next.filters,
      },
    },
  };
};

// ---------------------------------------------------------------------------
// Save serialization
// ---------------------------------------------------------------------------

const pruneText = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

// Empty containers are dropped so the payload matches what the validator would produce anyway:
// badge survives only with non-blank text, highlight needs a value, action needs a label, and an
// empty display object is indistinguishable from an absent one.
const pruneDisplay = (display: Display | undefined): Display | undefined => {
  if (!display) {
    return undefined;
  }

  let next: Display = {};
  next = withOptional(next, 'title', pruneText(display.title));
  next = withOptional(next, 'subtitle', pruneText(display.subtitle));
  next = withOptional(next, 'description', pruneText(display.description));

  const badgeText = pruneText(display.badge?.text);
  if (badgeText) {
    const badge: NonNullable<Display['badge']> = { text: badgeText };
    next = withOptional(next, 'badge', withOptional(badge, 'variant', display.badge?.variant));
  }

  const highlightValue = pruneText(display.highlight?.value);
  if (highlightValue) {
    const highlight: NonNullable<Display['highlight']> = { value: highlightValue };
    next = withOptional(
      next,
      'highlight',
      withOptional(highlight, 'caption', pruneText(display.highlight?.caption))
    );
  }

  const actionLabel = pruneText(display.action?.label);
  if (actionLabel) {
    const action: NonNullable<Display['action']> = { label: actionLabel };
    next = withOptional(next, 'action', withOptional(action, 'href', pruneText(display.action?.href)));
  }

  const tags = (display.tags ?? []).map(tag => tag.trim()).filter(Boolean);
  next = withOptional(next, 'tags', tags.length ? asTags(tags) : undefined);

  return Object.keys(next).length ? next : undefined;
};

export const pruneDraftForSave = (definition: DashboardDefinition): DashboardDefinition => ({
  ...definition,
  components: definition.components.map(component =>
    withEnvelope(component, { display: pruneDisplay(component.display) })
  ),
});
